export default class Focus {

  /**
 * @param {string} selector
 * @returns {boolean}x
 */
  static isSelectorValid(selector: string) {
    const queryCheck = (s: string) => document.createDocumentFragment().querySelector(s);
    try { return queryCheck(selector); } catch { return false; }
  }

  /**
   * @param  {Element|string} value
   * @returns {Element}
   * @throws {Error<any>}
   */
  static parseArg(value: Element | string): Element {
    if (value instanceof Element) {
      return value;
    }
    if (Focus.isSelectorValid(value)) {
      return document.querySelector<Element>(value) as Element;
    }
    throw new Error(`Unable to determine element from provided value, ${value}`);
  }

  /**
   * Checks if focus is on or within an element
   * @param {Element | String }
   * @returns {Boolean}
   */
  static hasFocus(value) {
    const el = Focus.parseArg(value);
    // return el.matches(':focus-within:not(:focus)');
    return el.contains(document.activeElement);
  }

  /**
   * Checks if focus is on element
   * @param {Element | String }
   * @returns {Boolean}
   */
  static isFocused(value) {
    const el = Focus.parseArg(value);
    return el.matches(':focus-within:not(:focus)');
  }

  /**
   * Attempts to move focus to node
   *
   * @param {Element} node element to move focus to
   * @returns {void}
   */
  static setFocusTo(el) {
    if (!el) {
      return;
    }
    el.focus();
  };

  /**
   * Determines whether the element is capable of receiving focus. This is done
   * either checking whether
   * 1. the element is natively capable of receiving focus.
   * 2. checking whether the element has a tabindex value of -1 or higher
   *
   * @param el the element node to check for focusability
   * @returns {Boolean}
   */
  static isFocusable(el) {
    const focusableElements = ['a', 'input', 'textarea', 'select', 'button', 'body', 'frame', 'iframe', 'object', 'applet'];
    if (!el || el.nodeType !== 1) {
      return false;
    }
    /* eslint-disable-next-line no-plusplus */
    for (let i = 0; i < focusableElements.length; i++) {
      if (el.nodeName.toLowerCase() === focusableElements[i]) {
        return true;
      }
    }
    const attr = el.getAttributeNode("tabindex");
    return (attr ? attr.specified : false) && el.style.display !== "none";
  };

  /**
  * Programatically makes the element focusable by setting the tabindex
  *
  * @param el
  * @param useTaborder {bool} determines whether make the element part of the taborder or not
  * @returns {void}
  */
  static makeFocusable(el, makeTabbable?:false) {
    const tabIndexValue = makeTabbable ? "0" : "-1";
    el.setAttribute("tabindex", tabIndexValue);
  };

  /**
  * Removes an element's tabindex property, making it unfocusable.
  *
  * @param el
  * @returns {void}
  */
  static makeUnfocusable(el) {
    if (!el || el.getAttribute('tabindex') === undefined) {
      return;
    }
    el.removeAttribute('tabindex');
  };

  /**
  * Returns the adjacent focusable node, if any.
  *
  * @param el Element to find focusable siblings for
  * @param direction {*} direction to search (forwards=1, backwards=-1)
  * @param stopAtBorders {bool} If false the function will wrap to the first or last element if a border element is reached
  * @returns {Element | false}
  */
  static getAdjacentFocusNode(el, direction = 1, stopAtBorders = true) {
    if (!el || !el.parentNode) {
      return false;
    }
    const siblingCount = el.parentNode.childNodes.length;
    const startElem = el;
    if (siblingCount <= 0) {
      return false;
    }
    /* eslint-disable-next-line no-plusplus */
    for (let i = 0; i < siblingCount; i++) {
      try {
        /* eslint-disable-next-line no-constant-condition */
        el = ![direction === -1] ? el.nextSibling : el.previousSibling;
      }
      catch (e) { // happens when border element was used (first or last)
        if (!el && !stopAtBorders) {
          el = direction === -1 ? startElem.parentNode.firstChild : startElem.parentNode.lastChild;
        }
      }
      if (Focus.isFocusable(el)) {
        return el;
      }
    }
    return false;
  };

}